// Copyright (c) StrangeBee 2023

import { useLocalMessage } from '@Helpers/locale';
import useValidateForm from '@Hooks/useValidateForm';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import React, { ReactElement } from 'react';
import { CaseBulkEditForm, CaseBulkEditValues } from './CaseBulkEditForm';
import { CustomStatusMapType } from 'thehive-sdk';
import { trimTags } from '@Helpers/tags';

interface CaseBulkEditManageProps {
    triggerElem: ReactElement;
    changeStatus?: boolean;
    caseStatus?: CustomStatusMapType;
    onEdit: (values: CaseBulkEditValues) => void;
}

const CaseBulkEditManage = ({
    triggerElem,
    changeStatus = false,
    caseStatus = {},
    onEdit,
}: CaseBulkEditManageProps): ReactElement => {
    const i = useLocalMessage();
    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const onFinish = (values: CaseBulkEditValues): void => {
        const valuesProcessed: any = Object.fromEntries(
            Object.entries(values).filter(([key]) => {
                return !key.includes('Checked') && values[`${key}Checked`];
            }),
        );

        onEdit({
            ...valuesProcessed,
            addTags: trimTags(valuesProcessed.addTags || []),
            removeTags: trimTags(valuesProcessed.removeTags || []),
        });

        form.resetFields();
        setFieldsTouched(false);
    };

    return (
        <THDrawer
            title={i('case.bulk-edit.drawer.title')}
            triggerElem={triggerElem}
            afterVisibleChange={() => form.resetFields()}
            className="drawer-form"
            disableClose={fieldsTouched}
        >
            {(toggleDrawer) => (
                <CaseBulkEditForm
                    onFinish={(values) => {
                        onFinish(values);
                        toggleDrawer();
                    }}
                    form={form}
                    onChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    isValid={isValid && fieldsTouched}
                    onCancel={() => {
                        form.resetFields();
                        toggleDrawer();
                        setFieldsTouched(false);
                    }}
                    changeStatus={changeStatus}
                    caseStatus={caseStatus}
                />
            )}
        </THDrawer>
    );
};

export default CaseBulkEditManage;
